/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module modals/QRCodeScanModal/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { useState, useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import styles from "./index.module.css";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as QRCodeScanIcon } from "../../assets/icons/QRCodeScanModal/QRCodeScanIcon.svg";
import { useNavigate } from "react-router";
import { CircularProgress, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CameraIcon from "@mui/icons-material/Camera";
import SettingsIcon from "@mui/icons-material/Settings";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import { useQRScanFlow } from "../../hooks/useQRScanFlow.js";

const QRCodeScanModal = ({ open, onClose, selectedEvent, onQRCodeScanned }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deviceType } = useDeviceDetection();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationStatus, setConfirmationStatus] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [permissionError, setPermissionError] = useState(null);
  const html5QrCodeRef = useRef(null);
  const lastScannedRef = useRef(null);
  const isProcessingRef = useRef(false);
  const cooldownTimeoutRef = useRef(null);

  const { onScanSuccess } = useQRScanFlow({
    selectedEvent,
    navigate,
    onQRCodeScanned,
    isProcessingRef,
    lastScannedRef,
    cooldownTimeoutRef,
    setIsLoading,
    setConfirmationStatus,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    setHasPermission(null);
    setPermissionError(null);
    setCameras([]);
    setSelectedCamera(null);

    checkPermissions();

    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current
          .stop()
          .catch((err) => {
            if (!err.message?.includes("scanner is not running")) {
              logger.error("Error stopping scanner:", {
                err: sanitizeError(err),
              });
            }
          })
          .finally(() => {
            html5QrCodeRef.current.clear();
            html5QrCodeRef.current = null;
          });
      }
      setConfirmationStatus(null);
    };
  }, [open]);

  useEffect(() => {
    if (hasPermission === true) {
      startScanner();
    }
  }, [hasPermission]);

  const checkPermissions = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setHasPermission(false);
        setPermissionError("browserNotSupported");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
        },
      });

      stream.getTracks().forEach((track) => track.stop());
      setHasPermission(true);
      setPermissionError(null);
    } catch (err) {
      setHasPermission(false);
      if (err.name === "NotAllowedError") {
        setPermissionError("permissionDenied");
      } else if (err.name === "NotFoundError") {
        setPermissionError("noCamera");
      } else {
        setPermissionError("unknown");
      }
      logger.error("Camera permission error:", { err: sanitizeError(err) });
    }
  };

  // Function to get qrbox dimensions based on device type
  const getQrboxDimensions = () => {
    switch (deviceType) {
      case "smallMobile":
        return { width: 120, height: 120 };
      case "mediumMobile":
        return { width: 130, height: 130 };
      case "tablet":
        return { width: 200, height: 200 };
      case "laptop":
        return { width: 180, height: 180 };
      case "desktop":
        return { width: 250, height: 250 };
      default:
        return { width: 250, height: 250 };
    }
  };

  const startScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        if (!err.message?.includes("scanner is not running")) {
          logger.error("Error stopping existing scanner:", {
            err: sanitizeError(err),
          });
        }
      }
      html5QrCodeRef.current.clear();
      html5QrCodeRef.current = null;
    }

    const config = {
      fps: 10,
      qrbox: getQrboxDimensions(),
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      rememberLastUsedCamera: true,
    };

    const qrCodeRegionId = "reader";

    try {
      const html5QrCode = new Html5Qrcode(qrCodeRegionId);
      html5QrCodeRef.current = html5QrCode;

      const availableCameras = await Html5Qrcode.getCameras();
      if (availableCameras && availableCameras.length) {
        setCameras(availableCameras);
        const backCamera = availableCameras.find(
          (camera) =>
            camera.label.toLowerCase().includes("back") ||
            camera.label.toLowerCase().includes("rear"),
        );
        const initialCamera = backCamera || availableCameras[0];
        setSelectedCamera(initialCamera);

        await html5QrCode.start(
          initialCamera.id,
          config,
          onScanSuccess,
          (err) => logger.warn("QR error", { err: sanitizeError(err) }),
        );
      } else {
        setPermissionError("noCamera");
      }
    } catch (err) {
      logger.error("Failed to start QR scanner:", { err: sanitizeError(err) });
      setPermissionError("unknown");
    }
  };

  const handlePermissionRequest = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
        },
      });
      stream.getTracks().forEach((track) => track.stop());
      setHasPermission(true);
      setPermissionError(null);
    } catch (err) {
      setPermissionError("permissionDenied");
    }
  };

  const getBrowserSettingsInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("chrome")) {
      return t("components.scanQRCodeModal.chromeSettings");
    } else if (userAgent.includes("firefox")) {
      return t("components.scanQRCodeModal.firefoxSettings");
    } else if (userAgent.includes("safari")) {
      return t("components.scanQRCodeModal.safariSettings");
    } else {
      return t("components.scanQRCodeModal.genericSettings");
    }
  };

  const switchCamera = async () => {
    if (!html5QrCodeRef.current || cameras.length < 2) {
      return;
    }

    const currentIndex = cameras.findIndex(
      (cam) => cam.id === selectedCamera.id,
    );
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];

    try {
      await html5QrCodeRef.current.stop();
      await html5QrCodeRef.current.start(
        nextCamera.id,
        {
          fps: 10,
          qrbox: getQrboxDimensions(),
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        },
        onScanSuccess,
        (err) => logger.warn("QR error", { err: sanitizeError(err) }),
      );
      setSelectedCamera(nextCamera);
    } catch (err) {
      logger.error("Failed to switch camera:", { err: sanitizeError(err) });
    }
  };

  useEffect(() => {
    if (confirmationStatus) {
      const timeoutId = setTimeout(() => {
        setConfirmationStatus(null);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [confirmationStatus]);

  useEffect(() => {
    return () => {
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, []);

  if (!open) {
    return null;
  }

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          <CloseIcon />
        </button>

        {hasPermission === false ? (
          <div className={styles.permissionContainer}>
            <CameraIcon className={styles.permissionIcon} />
            {permissionError === "browserNotSupported" && (
              <p>{t("components.scanQRCodeModal.browserNotSupported")}</p>
            )}
            {permissionError === "permissionDenied" && (
              <>
                <p>{t("components.scanQRCodeModal.cameraPermissionDenied")}</p>
                <div className={styles.settingsInstructions}>
                  <SettingsIcon className={styles.settingsIcon} />
                  <p>{getBrowserSettingsInstructions()}</p>
                </div>
              </>
            )}
            {permissionError === "noCamera" && (
              <p>{t("components.scanQRCodeModal.noCameraFound")}</p>
            )}
            {permissionError === "unknown" && (
              <p>{t("components.scanQRCodeModal.cameraError")}</p>
            )}
            <button
              className={styles.grantPermissionButton}
              onClick={handlePermissionRequest}
            >
              {t("components.scanQRCodeModal.grantPermission")}
            </button>
          </div>
        ) : (
          <>
            <div className={styles.scannerContainer}>
              <div id="reader" className={styles.qrScanner}></div>
            </div>
            {cameras.length > 1 && (
              <button
                className={styles.cameraSwitchButton}
                onClick={switchCamera}
              >
                <CameraAltIcon />
                {t("components.scanQRCodeModal.switchCamera")}
              </button>
            )}

            <div className={styles.instructionsContainer}>
              <div className={styles.instructionRow}>
                <QRCodeScanIcon className={styles.instructionIcon} />
                <p className={styles.instruction1Text}>
                  {t("components.scanQRCodeModal.instruction1")}
                </p>
              </div>
              <p className={styles.instruction2Text}>
                {t("components.scanQRCodeModal.instruction2")}
              </p>
            </div>

            {isLoading && (
              <div className={styles.loadingContainer}>
                <CircularProgress size={24} sx={{ color: "#2f59df" }} />
                <p>{t("components.scanQRCodeModal.confirmingAttendance")}</p>
              </div>
            )}

            {confirmationStatus === "success" && (
              <div
                className={`${styles.statusContainer} ${styles.successContainer}`}
              >
                <p>
                  {t(
                    "components.scanQRCodeModal.attendanceConfirmationSuccess",
                  )}
                </p>
              </div>
            )}
            {confirmationStatus === "error" && (
              <div
                className={`${styles.statusContainer} ${styles.errorContainer}`}
              >
                <p>
                  {t("components.scanQRCodeModal.attendanceConfirmationError")}
                </p>
              </div>
            )}
            {confirmationStatus === "invalidQRCode" && (
              <div
                className={`${styles.statusContainer} ${styles.errorContainer}`}
              >
                <p>{t("components.scanQRCodeModal.invalidQRCode")}</p>
              </div>
            )}
            {confirmationStatus === "invalidEvent" && (
              <div
                className={`${styles.statusContainer} ${styles.errorContainer}`}
              >
                <p>{t("components.scanQRCodeModal.invalidEvent")}</p>
              </div>
            )}
            {confirmationStatus === "appointmentRejected" && (
              <div
                className={`${styles.statusContainer} ${styles.errorContainer}`}
              >
                <p>{t("components.scanQRCodeModal.appointmentRejected")}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QRCodeScanModal;
